import React, { useState } from "react";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  Grid,
  Grid2,
  MenuItem,
  Select,
  Typography,
  useTheme,
  Skeleton
} from "@mui/material";
import Highcharts from "highcharts";
import HighchartsReact from "highcharts-react-official";
import { useParams } from "react-router-dom";
import { IoIosArrowDown } from "react-icons/io";
import { useGetMachinStatusQuery } from "store/slices/apis/pmDashboardSlice";
import { ReactComponent as ChevronDownIcon } from "assets/PwC_Funct_Icons_ChevronDown_Outline_Black_RGB.svg"



const MachineStatus = () => {
  const { projectId } = useParams();
  const theme = useTheme();
  const [selectedCategory, setSelectedCategory] = useState("All Category");

  const { data, isLoading, error } = useGetMachinStatusQuery({ projectId });


  const machineList = Array.isArray(data?.data) ? data.data : [];


  const allCategories = [...new Set(machineList.map((item) => item.pmType))];


  const onCount = machineList.map((item) => item.onCount);
  const offCount = machineList.map((item) => item.offCount);
  const idleCount = machineList.map((item) => item.idleCount);



  const isArrayAllZero = (array) => array.every((item) => item === 0);
  const isAllZero =
    isArrayAllZero(onCount) &&
    isArrayAllZero(offCount) &&
    isArrayAllZero(idleCount);

  const fullSeries = [
    {
      name: "ON",
      data: onCount,
      color: "#2D6A4F",
      pointWidth: 70,
      dataLabels: {
        enabled: true,
        verticalAlign: "top",
        y: 5,
        style: {
          fontSize: "14px",
          fontWeight: 600,
          textOutline: "none",
        },
      },
    },
    {
      name: "IDLE",
      data: idleCount,
      color: "#F9D71C",
      pointWidth: 70,
      dataLabels: {
        enabled: true,
        verticalAlign: "top",
        y: 7,
        style: {
          fontSize: "14px",
          fontWeight: 600,
          textOutline: "none",
        },
      },
    },
    {
      name: "OFF",
      data: offCount,
      color: "#E72B33",
      pointWidth: 70,
      dataLabels: {
        enabled: true,
        verticalAlign: "top",
        y: 7,
        style: {
          fontSize: "14px",
          fontWeight: 600,
          textOutline: "none",
        },
      },
    },
  ];

  const filteredIndex = allCategories.indexOf(selectedCategory);
  const filteredCategories =
    selectedCategory === "All Category" ? allCategories : [selectedCategory];



  const filteredSeries =
    selectedCategory === "All Category"
      ? fullSeries
      : fullSeries.map((series) => ({
        ...series,
        data: [series.data[filteredIndex]],
      }));

  const chartOptions = {
    chart: {
      type: "column",
      // height: 320,
    },
    title: {
      text: null,
    },
    xAxis: {
      categories: filteredCategories,
      tickmarkPlacement: "on",
      tickWidth: 1,
      tickLength: 10,
      tickColor: "#999",
      lineWidth: 1,
      lineColor: "#758195",
      labels: {
        style: {
          fontWeight: 500,
          fontSize: "12px",
          color: "#000000",
          opacity: 0.5,
          lineHeight: "100%",
        },
      },
    },
    yAxis: {
      min: 0,
      gridLineWidth: 1,
      tickInterval: 50,
      tickWidth: 1,
      tickLength: 10,
      tickColor: "#999",
      lineWidth: 1,
      lineColor: "#758195",
      title: {
        text: "Machine Count",
        style: {
          color: "#C05222",
          fontWeight: "500",
          fontSize: "16px",
        },
      },
      labels: {
        style: {
          fontWeight: 500,
          fontSize: "10px",
          color: "#414D55",
          opacity: 0.5,
          lineHeight: "100%",
        },
      },
    },
    legend: {
      enabled: true,
      align: "left",
      verticalAlign: "top",
      reversed: true,
      layout: "horizontal",
      itemDistance: 20,
      symbolRadius: 0,
      symbolHeight: 12,
      symbolWidth: 12,
      margin: 43,
      itemStyle: {
        fontWeight: 500,
        fontSize: "13px",
        color: "#182A4E",
      },
    },
    tooltip: {
      pointFormat: "{series.name}: <b>{point.y}</b><br/>",
      shared: true,
    },
    plotOptions: {
      column: {
        stacking: "normal",
        borderWidth: 0,
        dataLabels: {
          enabled: true,
          color: "#FFFFFF",
          style: {
            fontWeight: "bold",
          },
        },
      },
    },
    credits: {
      enabled: false,
    },
    series: filteredSeries,
  };


     const CustomChevronIcon = (props) => (
            <ChevronDownIcon style={{ width: '16px', height: '16px' }} {...props} />
        );

  return (
    <Box
      sx={{
        padding: 2,
        background: "#fff",
        borderRadius: 2,
        mb: 2,
        // height: "450px",
        width: "100%",
        minHeight: "100%",
      }}
    >
      <Grid2 container>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Typography
            variant="body"
            sx={{
              fontWeight: 500,
              fontSize: "18px",
              lineHeight: "100%",
              letterSpacing: "-0.4px",
              color: `${theme?.customSettings?.themeColor}`,
            }}
          >
            Machine Status
          </Typography>
          <Typography
            sx={{
              mt: "5px",
              color: "#5052588A",
              fontSize: "14px",
              mb: "20px",
              lineHeight: "100%",
              letterSpacing: "-0.4px",
              whiteSpace: { xs: "normal", md: "nowrap" },
            }}
          >
            Machine Status refers to the operational state of a machine, indicating <br />
            its availability and functionality
          </Typography>
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Box sx={{ width: "100%", display: "flex", justifyContent: "end" }}>
            <Select
              labelId="demo-simple-select-standard-label"
              id="demo-simple-select-standard"
              //value={10}
              // IconComponent={IoIosArrowDown}
               IconComponent={CustomChevronIcon}
              sx={{
                backgroundColor: "#E8E8E8",
                "& .MuiOutlinedInput-input": {
                  padding: "5px 15px",
                },
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
              }}
              value={selectedCategory}
              onChange={(e) => setSelectedCategory(e.target.value)}
            >
              <MenuItem value="All Category">All Category</MenuItem>
              {allCategories.map((category) => (
                <MenuItem key={category} value={category}>
                  {category}
                </MenuItem>
              ))}
            </Select>
          </Box>
        </Grid2>
      </Grid2>

      <Grid2 container>
        <Grid2 size={{ xs: 12, md: 12 }}>
          {isLoading ? (
            <Skeleton variant="rectangular" height={320} width="100%" />
          ) : error ? (
            <Typography color="error" sx={{ textAlign: "center", mt: 5 }}>
              Error loading data
            </Typography>
          ) : isAllZero ? (
            <Box sx={{ width: '100%', textAlign: 'center', py: 4 }}>
              <Typography variant="body1" color="textSecondary">
                No data available to display the chart.
              </Typography>
            </Box>
          )
            : (
              <HighchartsReact highcharts={Highcharts} options={chartOptions} />
            )}
        </Grid2>
      </Grid2>
    </Box>
  );
};

export default MachineStatus;
